import { useMemo } from "react";

import { useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import useSignalsRestApiData from "app/pages/sales-intelligence/modules/manage-integrations/hooks/useSignalsRestApiData";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";

import { SIBreadcrumbs } from "app/pages/sales-intelligence/common-components/SIBreadcrumbs/SIBreadcrumbs";
import { MANAGEMENT_GATEWAY } from "app/pages/sales-intelligence/constants/sl_routes";
import type { ApiPageType } from "app/pages/sales-intelligence/modules/manage-integrations/types/api-page-type";
import {
    StyledGatewayActionsRow,
    StyledGatewayLearnMoreButton,
    StyledGatewayPageList,
    StyledGatewayPageWrapper,
    StyledHowDoesItWorkLine,
} from "app/pages/sales-intelligence/pages/management/containers/GatewayPageContainer/styles";
import type { RequestAdminFeatureBase } from "app/pages/sales-intelligence/types/requestAdminFeature";

import {
    CONTENT_BY_PAGE_TYPE,
    HOW_DOES_IT_WORK,
} from "app/pages/sales-intelligence/pages/management/constants/gateway-pages";
import {
    resolveGatewayFooterActionButton,
    resolveGatewayHeaderActionButton,
} from "app/pages/sales-intelligence/pages/management/utils/resolveGatewayHeaderActionButton";
import { CrmGatewayAutoRedirect } from "app/pages/sales-intelligence/pages/management/containers/GatewayPageContainer/CrmGatewayAutoRedirect";
import { StickyGatewayActionsBar } from "app/pages/sales-intelligence/pages/management/containers/GatewayPageContainer/StickyGatewayActionsBar";
import manageIntegrationsTrackingService from "app/pages/sales-intelligence/modules/manage-integrations/services/tracking";
import openExternalLink from "app/pages/sales-intelligence/modules/manage-integrations/utils/openExternalLink";
import { isCrmGatewayFeatureName } from "app/pages/sales-intelligence/pages/management/utils/isCrmGatewayFeatureName";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type GatewayFeatureName = ApiPageType | RequestAdminFeatureBase;

function GatewayPageContainer(): React.ReactNode {
    const { translate } = useTranslate();
    const { params } = useCurrentStateAndParams<{ featureName: GatewayFeatureName }>();

    const content = CONTENT_BY_PAGE_TYPE[params.featureName];

    const breadcrumbs = useMemo(() => {
        if (!content) {
            return [];
        }

        return [
            {
                state: content.breadcrumbParent.route,
                title: translate(content.breadcrumbParent.titleKey),
            },
            {
                state: MANAGEMENT_GATEWAY,
                title: translate(content.titleKey),
            },
        ];
    }, [content, translate]);

    const signalsRestApiData = useSignalsRestApiData();

    useEffect(() => {
        if (params.featureName === "signalsRestApi" && signalsRestApiData.isConfigured) {
            void siNavigator.goToManagementSignalsRestApiPage();
        }
    }, [params.featureName, signalsRestApiData.isConfigured]);

    if (!content) {
        return null;
    }

    const {
        titleKey,
        descriptionKey,
        infoTitleKey,
        infoItemsKeys,
        creditsTitleKey,
        creditsItemsKeys,
        tutorialLink,
        renderInfoIllustration,
        renderCreditsIllustration,
        renderActionButton,
        renderStickyActionButton,
    } = content;

    const handleBreadcrumbClick = ({ state }: { state: string }): void => {
        if (!state || state === MANAGEMENT_GATEWAY) {
            return;
        }

        void proNavigator.go(state);
    };

    const infoIllustration = renderInfoIllustration();
    const creditsIllustration = renderCreditsIllustration();

    const handleLearnMore = () => {
        manageIntegrationsTrackingService.trackGatewayLearnMore(params.featureName);

        if (tutorialLink) {
            openExternalLink(tutorialLink);
        }
    };

    const renderGatewayActions = (actionButton: React.ReactNode) => (
        <StyledGatewayActionsRow>
            {tutorialLink ? (
                <StyledGatewayLearnMoreButton
                    iconName="helpcenter"
                    type="flat"
                    onClick={handleLearnMore}
                    data-automation="gateway-page-learn-more-button"
                >
                    {translate("si.common.learn_more")}
                </StyledGatewayLearnMoreButton>
            ) : null}
            {actionButton}
        </StyledGatewayActionsRow>
    );

    const renderInfoBlock = (
        title: string,
        items: readonly string[],
        illustration: React.ReactElement,
        isReverse = false,
    ) => (
        <Flex
            width="100%"
            gap="24px"
            alignItems="center"
            flexDirection={isReverse ? "row-reverse" : "row"}
        >
            {illustration}
            <Stack
                gap="8px"
                flex="1 1 auto"
                minWidth="0"
            >
                <Text
                    fontVariant="h4"
                    color={colorsPalettes.carbon["400"]}
                >
                    {translate(title)}
                </Text>
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon["400"]}
                >
                    <StyledGatewayPageList>
                        {items.map((item, i) => (
                            <li
                                key={i}
                                data-automation-i18n-key={item}
                                dangerouslySetInnerHTML={{
                                    __html: translate(item),
                                }}
                            />
                        ))}
                    </StyledGatewayPageList>
                </Text>
            </Stack>
        </Flex>
    );

    const gatewayActionButtonRenderers = { renderActionButton, renderStickyActionButton };

    const headerActionButton = resolveGatewayHeaderActionButton(gatewayActionButtonRenderers);
    const footerActionButton = resolveGatewayFooterActionButton(gatewayActionButtonRenderers);

    return (
        <div>
            {isCrmGatewayFeatureName(params.featureName) ? (
                <CrmGatewayAutoRedirect integrationName={params.featureName} />
            ) : null}

            <SIBreadcrumbs
                list={breadcrumbs}
                onClick={handleBreadcrumbClick}
                dataAutomation="gateway-page-breadcrumbs"
            />

            <StyledGatewayPageWrapper $hasFixedFooter={Boolean(renderStickyActionButton)}>
                <Stack gap="60px">
                    <Stack
                        gap="8px"
                        alignItems="center"
                        width="100%"
                    >
                        <Text
                            fontVariant="h2"
                            textAlign="center"
                            color={colorsPalettes.black["0"]}
                        >
                            {translate(titleKey)}
                        </Text>
                        <Text
                            fontVariant="body1"
                            textAlign="center"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {translate(descriptionKey)}
                        </Text>
                    </Stack>

                    {renderGatewayActions(headerActionButton)}

                    <Flex
                        justifyContent="center"
                        alignItems="center"
                        gap="8px"
                        position="relative"
                        width="100%"
                    >
                        <StyledHowDoesItWorkLine />
                        <Text
                            fontVariant="body1"
                            color={colorsPalettes.carbon["300"]}
                        >
                            {translate(HOW_DOES_IT_WORK)}
                        </Text>
                        <StyledHowDoesItWorkLine />
                    </Flex>

                    {renderInfoBlock(infoTitleKey, infoItemsKeys, infoIllustration)}

                    {renderInfoBlock(creditsTitleKey, creditsItemsKeys, creditsIllustration, true)}
                </Stack>

                {renderStickyActionButton ? (
                    <StickyGatewayActionsBar>{renderGatewayActions(footerActionButton)}</StickyGatewayActionsBar>
                ) : (
                    renderGatewayActions(footerActionButton)
                )}
            </StyledGatewayPageWrapper>
        </div>
    );
}

export default GatewayPageContainer;
